import 'animate.css';

import { Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';

import { StopsActionsMenu } from '../../../../components/menus/StopsActionsMenu';
import { StopsCreateMenu } from '../../../../components/menus/StopsCreateMenu';
import { PLAN_TYPE } from '../../../../constants/Subscriptions';
import { ProjectStopsTableColumnKeys } from '../../../../context/project/tableColumns';
import { sortDataByTableColumns, TableColumnsSortConfig } from '../../../../context/shared/tableColumns';
import { Stop } from '../../../../domain/stop/Stop';
import { useRetrieveOrganization } from '../../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveFlatStops } from '../../../../hooks/stop/useRetrieveFlatStops';
import { useSearchProjectStopsPredicates } from '../../../../hooks/stop/useSearchProjectStopsPredicates';
import { useSearchStops } from '../../../../hooks/stop/useSearchStops';
import { useSetSelectedProjectStops } from '../../../../hooks/stop/useSetSelectedProjectStops';
import { useRetrieveAllSupervisors } from '../../../../hooks/supervisor/useRetrieveAllSupervisors';
import { useRetrieveFlatVehicles } from '../../../../hooks/vehicle/useRetrieveFlatVehicles';
import { DataTable } from '../../../../react-components/data-table/DataTable';
import Text from '../../../../react-components/Text';
import StopCircleColor from '../../../shared/StopCircleColor';
import { getPlannerStopsColumns } from './PlannerStopsListColumns';

const LIMIT = 20;

interface PlannerStopsListProps {
  geoFenceFilters?: string[];
}

export const PlannerStopsList = ({ geoFenceFilters }: PlannerStopsListProps) => {
  const { setRightPanelInfo } = useRetrieveRightPanelInfo();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: organization } = useRetrieveOrganization();
  const { data: projectVehicles } = useRetrieveFlatVehicles({ project: activeProject! });
  const { data: supervisors } = useRetrieveAllSupervisors();
  const { selectedStops, setSelectedStops } = useSetSelectedProjectStops();
  const { predicates } = useSearchProjectStopsPredicates();

  const startDatePredicate = predicates.find((p) => p.field === `created_at` && p.operator === `gte`);
  const startDate = useMemo(
    () =>
      startDatePredicate?.value
        ? new Date(startDatePredicate.value as string)
        : moment(new Date()).subtract(`1`, `month`).toDate(),
    [startDatePredicate]
  );
  const endDatePredicate = predicates.find((p) => p.field === `created_at` && p.operator === `lte`);
  const endDate = useMemo(
    () => (endDatePredicate?.value ? new Date(endDatePredicate.value as string) : new Date()),
    [endDatePredicate]
  );

  const [tableContextualAnchor, setTableContextualAnchor] = useState<
    { screenPosition: { top: number; left: number }; stop: Stop } | undefined
  >(undefined);

  const [searchParameters, setSearchParameters] = useState<{ limit: number; offset: number }>({
    offset: 0,
    limit: LIMIT,
  });

  const [sortStopsInfo, setSortStopsInfo] = useState<TableColumnsSortConfig<ProjectStopsTableColumnKeys>>({
    orderBy: `created_at`,
    order: `desc`,
  });

  const { data: flatStops, run: retrieveFlatStops } = useRetrieveFlatStops({
    project: activeProject,
    geoFences: geoFenceFilters,
  });

  const {
    data: stopsPagination,
    isLoading,
    run: searchStops,
    tableColumns,
  } = useSearchStops({
    project: activeProject,
    offset: searchParameters.offset,
    limit: searchParameters.limit,
    sortBy: sortStopsInfo.orderBy,
    sortDirection: sortStopsInfo.order,
    geoFences: geoFenceFilters,
  });

  useEffect(() => {
    if (searchParameters.offset !== 0) {
      // Reset pagination because dates range has changed.
      setSearchParameters({ offset: 0, limit: LIMIT });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    if (predicates.length > 0) {
      searchStops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject, searchParameters, sortStopsInfo, geoFenceFilters]);

  if (!activeProject || !stopsPagination || !organization) {
    return null;
  }

  if (isLoading || stopsPagination.total > 0) {
    return (
      <div
        style={{
          padding: `0 22px 22px`,
          height: `100%`,
          backgroundColor: `white`,
          width: `calc(100% - 40px)`,
        }}
      >
        <Grid
          container
          style={{
            // padding: `4px 0px 4px 8px`,
            height: `calc(100vh - 207px)`,
            backgroundColor: `white`,
            overflowX: `auto`,
            borderRadius: `10px`,
          }}
          direction="column"
        >
          <Grid item container style={{ overflowX: `auto`, overflowY: `hidden`, height: `100% `, width: `100%` }}>
            <Grid container style={{ width: `100%`, height: `100%`, position: `relative` }}>
              <div style={{ height: `calc(100% - 43px)`, width: `100%`, overflowX: `auto`, position: `absolute` }}>
                <DataTable
                  docs={sortDataByTableColumns({
                    data: stopsPagination.docs,
                    tableColumns,
                    sortInfo: sortStopsInfo,
                  }).map((stop: Stop) => {
                    const checkboxIcon = (
                      <StopCircleColor
                        isPickup={stop.type === `pickup`}
                        number={stop.order !== undefined ? stop.order + 1 : undefined}
                      />
                    );
                    return {
                      doc: stop,
                      checkboxIcon: {
                        element: checkboxIcon,
                      },
                    };
                  })}
                  maxHeight="100vh"
                  loading={isLoading}
                  total={stopsPagination.total}
                  limit={stopsPagination.limit}
                  page={stopsPagination.page}
                  pages={stopsPagination.pages}
                  emptyMessage={<Text intlId="no_results" />}
                  handleOnContextMenu={(ev: any, stop: Stop) => {
                    ev.preventDefault();
                    setTableContextualAnchor({ screenPosition: { top: ev.clientY, left: ev.clientX }, stop });
                  }}
                  selectedIds={selectedStops}
                  onMultipleSelection={({ selectedIds: newSelection, fromGlobalCheckbox }) => {
                    if (fromGlobalCheckbox) {
                      if (selectedStops.length > 0) {
                        setSelectedStops([]);
                      } else {
                        setSelectedStops((flatStops ?? []).map((fS) => fS.id));
                      }
                    } else {
                      setSelectedStops(newSelection ?? []);
                    }
                  }}
                  onRowDoubleClick={(stop: Stop) => {
                    setRightPanelInfo({
                      detailType: `stop`,
                      detailId: `${stop.id}`,
                      detailTab: null,
                    });
                  }}
                  onChangePage={({ offset, limit }) =>
                    setSearchParameters({
                      ...searchParameters,
                      offset,
                      limit,
                    })
                  }
                  sortBy={sortStopsInfo.orderBy}
                  sortDirection={sortStopsInfo.order}
                  onColumnSort={(column: any, order: `asc` | `desc`) => {
                    setSortStopsInfo({ order, orderBy: column });
                  }}
                  columns={getPlannerStopsColumns({
                    activeProject,
                    organization,
                    projectVehicles,
                    supervisors,
                    tableColumns,
                    onOpenRightPanel: (e, doc) => {
                      e.stopPropagation();
                      setRightPanelInfo({
                        detailType: `stop`,
                        detailId: `${doc.id}`,
                        detailTab: null,
                      });
                    },
                    onDeleteStops: (deletedStopsIds: string[]) => {
                      retrieveFlatStops();
                      searchStops();
                    },
                  })}
                />

                {tableContextualAnchor ? (
                  <StopsActionsMenu
                    type="contextual"
                    stops={[tableContextualAnchor.stop]}
                    contextAnchor={tableContextualAnchor}
                    showAssignToDriver={false}
                    showOpenPlan={true}
                    showChangeConstraints={false}
                    showOpenCustomers={organization.subscription.plan !== PLAN_TYPE.MONKEY}
                    setContextAnchor={() => {
                      setTableContextualAnchor(undefined);
                    }}
                    afterDeleteStops={(deletedStopsIds: string[]) => {
                      retrieveFlatStops();
                      searchStops();
                    }}
                  />
                ) : null}
              </div>
            </Grid>
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <Grid container direction="column" alignItems="center" justifyContent="center" style={{ height: `100%` }}>
      <VSpacer medium />

      <Grid container direction="column" justifyContent="center" style={{ textAlign: `center` }}>
        <Text variant="h6" isBold intlId="planner.stops.no_stops.title" />

        <VSpacer medium />

        <Text variant="body-medium" intlId="planner.stops.no_stops.info" />
      </Grid>

      <VSpacer medium />

      <Grid container justifyContent="center">
        <StopsCreateMenu
          id="no-stops-create-actions"
          intlId="planner.stops.add_new"
          style={{
            backgroundColor: RoutalPalette.secondary40,
            justifyContent: `space-between`,
          }}
          hideSeparator
          containerStyle={{
            width: `unset`,
          }}
        />
      </Grid>
    </Grid>
  );
};
